/* eslint-disable i18next/no-literal-string -- wire values, not copy */
import type { TFunction } from 'i18next';
import { z } from 'zod';

import type {
  ReelBrandLook,
  ReelBrandLookInput as LookInputDto,
  ReelBrandStyleProfile,
} from '@/entities/reel-brand';

export const TRANSITION_TYPES = [
  'cut',
  'match_cut',
  'dissolve',
  'look_switch',
  'world_morph',
] as const;
export type TransitionType = (typeof TRANSITION_TYPES)[number];

const LOOK_KEY = /^[a-z][a-z0-9_]{0,39}$/;
export const MAX_NEVER_SHOW = 30;
export const MAX_NEVER_SHOW_LENGTH = 80;
export const MAX_LOOK_REFERENCES = 12;
export const MAX_PALETTE = 12;
export const MAX_PALETTE_NAME = 40;
export const MAX_LOOKS = 8;
const HEX = /^#[0-9a-fA-F]{6}$/;

const paletteSchema = z
  .array(z.object({ hex: z.string().regex(HEX), name: z.string().trim().max(MAX_PALETTE_NAME) }))
  .max(MAX_PALETTE);
export type PaletteValues = z.infer<typeof paletteSchema>;

export const buildLookSchema = (t: TFunction<'reelsCreator'>, takenKeys: readonly string[]) =>
  z.object({
    name: z.string().trim().min(1, t('reelBrandKit.fieldRequired')).max(80),
    key: z
      .string()
      .trim()
      .regex(LOOK_KEY, t('reelBrandKit.lookSheet.keyInvalid'))
      .refine((key) => !takenKeys.includes(key), t('reelBrandKit.lookSheet.keyTaken')),
    description: z.string().max(2000),
    imageDirective: z.string().max(2000),
    characterDirective: z.string().max(3000),
    videoDirective: z.string().max(400),
    neverShow: z.array(z.string().trim().min(1).max(MAX_NEVER_SHOW_LENGTH)).max(MAX_NEVER_SHOW),
    soundCues: z.string().max(600),
    isDefault: z.boolean(),
    palette: paletteSchema,
    references: z
      .array(z.object({ assetId: z.string().min(1), url: z.string().nullable() }))
      .max(MAX_LOOK_REFERENCES),
  });

export type LookValues = z.infer<ReturnType<typeof buildLookSchema>>;

/** A key from a name, so a new look needs no typing beyond its name. */
export const keyFromName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 40);

export const lookValuesOf = (look: ReelBrandLook | undefined, isFirst: boolean): LookValues => ({
  name: look?.name ?? '',
  key: look?.key ?? '',
  description: look?.description ?? '',
  imageDirective: look?.imageDirective ?? '',
  characterDirective: look?.characterDirective ?? '',
  videoDirective: look?.videoDirective ?? '',
  neverShow: look?.neverShow ?? [],
  soundCues: look?.soundCues ?? '',
  isDefault: look?.isDefault ?? isFirst,
  palette: look?.palette ?? [],
  references: (look?.referenceAssetIds ?? []).map((assetId, index) => ({
    assetId,
    url: look?.referenceUrls[index] ?? null,
  })),
});

/**
 * What a look sends back on a save: every field it was read with except the server's own, so a
 * field this form does not edit (the palette, or one a later server adds) survives the save.
 */
const SERVER_FIELDS: ReadonlySet<string> = new Set([
  'migrated',
  'referenceUrls',
  'previewAssetIds',
  'previewUrls',
  'approvedAt',
  'approvalStale',
]);

const toInput = (look: ReelBrandLook): LookInputDto =>
  Object.fromEntries(
    Object.entries(look).filter(([field]) => !SERVER_FIELDS.has(field)),
  ) as LookInputDto;

/** A word added to the never-show list, unless it is blank or already there in any case. */
export const neverShowWith = (list: readonly string[], word: string): string[] => {
  const trimmed = word.trim();
  if (!trimmed || list.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
    return [...list];
  }
  return [...list, trimmed];
};

/**
 * The brand's looks with one saved: a new key is added, an existing one replaced in place. A look
 * made the default takes the default from every other.
 */
export function looksWith(
  profile: ReelBrandStyleProfile,
  editingKey: string | undefined,
  values: LookValues,
): LookInputDto[] {
  const current = profile.looks.find((look) => look.key === editingKey);
  const saved: LookInputDto = {
    ...(current ? toInput(current) : {}),
    key: current?.key ?? values.key.trim(),
    name: values.name.trim(),
    isDefault: values.isDefault,
    description: values.description.trim(),
    imageDirective: values.imageDirective.trim(),
    characterDirective: values.characterDirective.trim(),
    videoDirective: values.videoDirective.trim(),
    neverShow: values.neverShow.map((item) => item.trim()),
    soundCues: values.soundCues.trim(),
    referenceAssetIds: values.references.map((reference) => reference.assetId),
    palette: values.palette.map((colour) => ({
      hex: colour.hex.toLowerCase(),
      name: colour.name.trim(),
    })),
  };
  const others = profile.looks.map(toInput).filter((look) => look.key !== saved.key);
  const looks = current
    ? profile.looks.map((look) => (look.key === saved.key ? saved : toInput(look)))
    : [...others, saved];
  if (!saved.isDefault) return looks;
  return looks.map((look) => ({ ...look, isDefault: look.key === saved.key }));
}

export const looksWithout = (profile: ReelBrandStyleProfile, key: string): LookInputDto[] =>
  profile.looks.filter((look) => look.key !== key).map(toInput);

export const allLooks = (profile: ReelBrandStyleProfile): LookInputDto[] =>
  profile.looks.map(toInput);

/** The fields a look's sample stills are drawn from, as the server compares them. */
export const previewSourceOf = (
  values: Pick<LookValues, 'imageDirective' | 'neverShow' | 'references'>,
): string =>
  JSON.stringify([
    values.imageDirective.trim(),
    values.neverShow.map((item) => item.trim()),
    values.references.map((reference) => reference.assetId),
  ]);

/** A key no other look holds: the one asked for, else it with the first free number after it. */
const freeKey = (wanted: string, taken: ReadonlySet<string>): string => {
  if (!taken.has(wanted)) return wanted;
  for (let n = 2; ; n += 1) {
    const candidate = `${wanted.slice(0, 40 - String(n).length - 1)}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
};

/** The brand's looks with the kept drafts added after them; the brand's default stays its default. */
export function looksWithDrafts(
  profile: ReelBrandStyleProfile,
  drafts: readonly LookInputDto[],
): LookInputDto[] {
  const taken = new Set(profile.looks.map((look) => look.key));
  const added = drafts.map((draft) => {
    const key = freeKey(draft.key, taken);
    taken.add(key);
    return { ...draft, key, isDefault: false };
  });
  return [...allLooks(profile), ...added];
}
